import { useState } from "react";
import { Button } from "components/DesignSystem/Button/Button";
import Card from "components/DesignSystem/Card/Card";
import { Tag } from "components/DesignSystem/Tag/Tag";
import Table from "components/DesignSystem/Table/V2/Table";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Divider } from "components/design/Divider";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { EditPencil } from "components/icons/EditPencil";
import { TrashSimple } from "components/icons/TrashSimple";
import Modal from "components/DesignSystem/Modal/Modal";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  flexRender,
} from "react-table-8.10.7";
import { IRSPayment } from "types/Models/estimatedTax";
import { ArrayElement } from "types/utils/ArrayElement";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { useDeleteIRSPaymentMutation } from "store/apis/estimatedTax";
import { LogPaymentModal } from "./LogPaymentModal";

type Props = {
  payments: IRSPayment[];
  totalFederalTax: number;
  ytdPayments: number;
  entityId: string;
  year: string;
};

const createColumn = createColumnHelper<ArrayElement<IRSPayment[]>>();

export const IRSPaymentTracker = ({
  payments,
  totalFederalTax,
  ytdPayments,
  entityId,
  year,
}: Props) => {
  const logModal = useModal();
  const [editingPayment, setEditingPayment] = useState<IRSPayment | null>(null);
  const [deletingPayment, setDeletingPayment] = useState<IRSPayment | null>(
    null
  );
  const [deletePayment, { isLoading: isDeleting }] =
    useDeleteIRSPaymentMutation();
  const { alertToast, successToast } = useToast();

  const handleDeleteConfirm = async () => {
    if (!deletingPayment) return;
    try {
      await deletePayment({ entityId, paymentId: deletingPayment.id }).unwrap();
      successToast({ message: "Payment deleted" });
      setDeletingPayment(null);
    } catch {
      alertToast({ message: "Failed to delete payment" });
    }
  };

  const rawPercent =
    totalFederalTax > 0
      ? Math.min(Math.round((ytdPayments / totalFederalTax) * 100), 100)
      : 0;
  const paidPercent = ytdPayments > 0 && rawPercent === 0 ? 1 : rawPercent;

  const columns = [
    createColumn.accessor("payment_date", {
      header: () => <HeadCellContent title="Date Paid" />,
      cell: (info) => (
        <span className="t-text-body-sm t-text-text-60">
          {dayjs(info.getValue()).format(DISPLAY_DATE)}
        </span>
      ),
      size: 18,
    }),
    createColumn.accessor("quarter", {
      header: () => <HeadCellContent title="Quarter" />,
      cell: (info) => {
        const q = info.getValue();
        if (q) {
          return (
            <Tag tagType="blue" icon={false} rounded size="small">
              {q}
            </Tag>
          );
        }
        return <span className="t-text-body-sm t-text-text-30">—</span>;
      },
      size: 10,
    }),
    createColumn.accessor("amount", {
      header: () => <HeadCellContent title="Amount" align="right" />,
      cell: (info) => (
        <div className="t-text-right">
          <AmountSuperScript amount={info.getValue()} />
        </div>
      ),
      size: 16,
    }),
    createColumn.accessor("payment_method", {
      header: () => <HeadCellContent title="Method" />,
      cell: (info) => (
        <span className="t-text-body-sm t-text-text-60">{info.getValue()}</span>
      ),
      size: 14,
    }),
    createColumn.accessor("confirmation_number", {
      header: () => <HeadCellContent title="Confirmation #" />,
      cell: (info) => {
        const val = info.getValue();
        if (val) {
          return (
            <span className="t-text-body-sm t-text-text-60 t-block t-truncate">
              {val}
            </span>
          );
        }
        return <span className="t-text-body-sm t-text-text-30">—</span>;
      },
      size: 28,
    }),
    createColumn.accessor("is_admin_entry", {
      header: () => <HeadCellContent title="Added By" />,
      cell: (info) => {
        const isAdmin = info.getValue();
        const { added_by_name } = info.row.original;
        if (isAdmin) {
          return (
            <Tag tagType="purple" icon={false} rounded size="small">
              Admin
            </Tag>
          );
        }
        return (
          <span className="t-text-body-sm t-text-text-30">
            {added_by_name ?? "—"}
          </span>
        );
      },
      size: 26,
    }),
    createColumn.accessor("receipt_url", {
      header: () => <HeadCellContent title="Receipt" />,
      cell: (info) => {
        const url = info.getValue();
        if (url) {
          return (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="t-text-purple t-text-body-sm t-underline"
            >
              View
            </a>
          );
        }
        return <span className="t-text-body-sm t-text-text-30">—</span>;
      },
      size: 8,
    }),
    createColumn.display({
      id: "actions",
      header: () => null,
      cell: (info) => (
        <div className="t-flex t-items-center t-gap-1">
          <Button
            customType="ghost"
            size="small"
            onClick={() => setEditingPayment(info.row.original)}
          >
            <EditPencil className="t-text-text-30" />
          </Button>
          <Button
            customType="ghost"
            size="small"
            onClick={() => setDeletingPayment(info.row.original)}
          >
            <TrashSimple className="t-text-text-30" />
          </Button>
        </div>
      ),
      size: 8,
    }),
  ];

  const table = useReactTable({
    data: payments,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <Card.Root>
        <Card.Header bordered={false}>
          <div className="t-flex t-items-center t-justify-between t-w-full">
            <Card.Title className="!t-text-body">Payment Tracker</Card.Title>
            <Button
              customType="primary_outlined"
              size="small"
              onClick={logModal.open}
            >
              Log New Payment
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="t-space-y-4">
          {/* Progress bar */}
          {totalFederalTax > 0 && (
            <div className="t-space-y-1.5">
              <div className="t-flex t-items-center t-justify-between t-text-body-sm">
                <span className="t-text-text-30">
                  Paid YTD:{" "}
                  <span className="t-font-medium t-text-text-60">
                    <AmountSuperScript amount={ytdPayments} />
                  </span>
                </span>
                <span className="t-text-text-30">
                  Annual estimate:{" "}
                  <span className="t-font-medium t-text-text-60">
                    <AmountSuperScript amount={totalFederalTax} />
                  </span>
                </span>
              </div>
              <div className="t-relative t-w-full t-h-2 t-flex t-items-center">
                <div className="t-absolute t-w-full">
                  <Divider type="dashed" color="grey" />
                </div>
                <div
                  className="t-absolute t-h-2 t-rounded-full t-bg-green-70 t-transition-all"
                  style={{ width: `${paidPercent}%` }}
                />
              </div>
              <div className="t-text-right t-text-body-sm t-text-text-30">
                {paidPercent}% of annual estimate paid
              </div>
            </div>
          )}

          {payments.length === 0 ? (
            <div className="t-text-center t-py-8 t-text-body-sm t-text-text-30">
              No IRS payments logged yet. Use the button above to record a
              payment.
            </div>
          ) : (
            <Table.Container sticky={false}>
              <Table.Content className="t-text-body-sm t-table-fixed t-w-full">
                <Table.Head>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <Table.HeadRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <Table.HeadCell
                          key={header.id}
                          style={{ width: `${header.getSize()}%` }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </Table.HeadCell>
                      ))}
                    </Table.HeadRow>
                  ))}
                </Table.Head>
                <Table.Body>
                  {table.getRowModel().rows.map((row) => (
                    <Table.Row key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <Table.Cell
                          key={cell.id}
                          style={{ width: `${cell.column.getSize()}%` }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Content>
            </Table.Container>
          )}
        </Card.Body>
      </Card.Root>

      {logModal.isOpen && (
        <LogPaymentModal {...logModal} entityId={entityId} year={year} />
      )}

      {editingPayment && (
        <LogPaymentModal
          isOpen
          close={() => setEditingPayment(null)}
          entityId={entityId}
          year={year}
          payment={editingPayment}
        />
      )}

      <Modal.Root
        open={!!deletingPayment}
        onOpenChange={() => setDeletingPayment(null)}
      >
        <Modal.Portal>
          <Modal.Overlay />
          <Modal.Content className="!t-p-0 md:!t-w-[440px]">
            <Modal.Header>
              <Modal.Title>Delete payment</Modal.Title>
              <Modal.Close />
            </Modal.Header>
            <Modal.Body>
              <p className="t-text-body-sm t-m-0 t-text-text-60">
                This payment will be permanently deleted and cannot be
                recovered. Are you sure you want to proceed?
              </p>
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Button onClick={() => setDeletingPayment(null)}>Cancel</Button>
              <Button
                customType="danger"
                isLoading={isDeleting}
                disabled={isDeleting}
                onClick={handleDeleteConfirm}
              >
                Delete
              </Button>
            </Modal.FooterButtonGroup>
          </Modal.Content>
        </Modal.Portal>
      </Modal.Root>
    </>
  );
};
